import { useState, useEffect } from 'react';
import fetchWithTenant from '../utils/fetchWithTenant';
import { 
  Users, Plus, Trash2, Edit2, Save, X, Shield, Eye, UserCog,
  Link2, Unlink, Camera, RefreshCw, ChevronDown, ChevronUp, DoorOpen, AlertTriangle, Phone, Sunset, Video, Server, CheckCircle, XCircle, Clock, Monitor, LogOut, Smartphone, Activity, Tablet, Loader2
} from 'lucide-react';
import { toast } from 'react-toastify';
import AdminUserSessionsModal from './AdminUserSessionsModal';

const UserManagement = ({ hideHeader = false }) => {
  const [users, setUsers] = useState([]);
  const [waveUsers, setWaveUsers] = useState([]);
  const [feenicsUsers, setFeenicsUsers] = useState([]);
  const [loadingFeenicsUsers, setLoadingFeenicsUsers] = useState(false);
  const [userReaders, setUserReaders] = useState({});
  const [loadingReaders, setLoadingReaders] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingWaveUsers, setLoadingWaveUsers] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [expandedUser, setExpandedUser] = useState(null);
  const [userCameras, setUserCameras] = useState({});
  const [loadingCameras, setLoadingCameras] = useState({});
  const [sessionsModalUser, setSessionsModalUser] = useState(null);
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    name: '',
    role: 'viewer',
    waveUserId: '',
    feenicsUserId: '',
    canLockdown: false,
    canAnswerIntercom: false,
    canActivateShabbat: false
  });
  
  // Tenant filtering
  const [tenants, setTenants] = useState([]);
  const [filterTenant, setFilterTenant] = useState('all');
  // VMS Integration state
  const [vmsIntegrations, setVmsIntegrations] = useState([]);
  const [integrationUsers, setIntegrationUsers] = useState({}); // { integrationId: [users] }
  const [userVmsLinks, setUserVmsLinks] = useState({}); // { odxjiaomdom0001g7um212ed4lk: [links] }
  const [loadingIntegrationUsers, setLoadingIntegrationUsers] = useState({});


  useEffect(() => {
    loadUsers();
    loadWaveUsers();
    loadFeenicsUsers();
    loadTenants();
    loadVmsIntegrations();
  }, []);

  const loadUsers = async () => {
    try {
      // Fetch database users (primary source)
      const dbResponse = await fetch('/api/platform/users', { credentials: 'include' });
      let dbUsers = [];
      if (dbResponse.ok) {
        dbUsers = await dbResponse.json();
      }
      
      // Transform DB users to match expected format
      const transformedUsers = dbUsers.map(user => ({
        id: user.id,
        username: user.email,
        email: user.email,
        name: user.name,
        role: user.isPlatformAdmin ? 'admin' : (user.orgRole?.toLowerCase() || 'viewer'),
        waveUserId: user.waveUserId,
        feenicsUserId: user.feenicsUserId,
        canLockdown: user.canLockdown,
        canAnswerIntercom: user.canAnswerIntercom,
        canActivateShabbat: user.canActivateShabbat,
        tenantAccess: user.tenantAccess,
        organization: user.organization
      }));
      
      setUsers(transformedUsers);
    } catch (error) {
      console.error('Failed to load users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadWaveUsers = async () => {
    setLoadingWaveUsers(true);
    try {
      const response = await fetchWithTenant('/api/wave/users');
      const data = await response.json();
      setWaveUsers(Array.isArray(data) ? data : data.users || []);
    } catch (error) {
      console.error('Failed to load Wave users:', error);
    } finally {
      setLoadingWaveUsers(false);
    }
  };

  const loadFeenicsUsers = async () => {
    setLoadingFeenicsUsers(true);
    try {
      const response = await fetchWithTenant('/api/feenics/users');
      const data = await response.json();
      setFeenicsUsers(Array.isArray(data) ? data : data.users || []);
    } catch (error) {
      console.error('Failed to load Feenics users:', error);
    } finally {
      setLoadingFeenicsUsers(false);
    }
  };

  // ==================== VMS INTEGRATION FUNCTIONS ====================
  
  const loadVmsIntegrations = async () => {
    try {
      const response = await fetchWithTenant('/api/vms/integrations');
      if (response.ok) {
        const data = await response.json();
        setVmsIntegrations(data.integrations || []);
        // Load users for each integration
        for (const integration of (data.integrations || [])) {
          loadIntegrationUsers(integration.id);
        }
      }
    } catch (error) {
      console.error('Failed to load VMS integrations:', error);
    }
  };

  const loadIntegrationUsers = async (integrationId) => {
    setLoadingIntegrationUsers(prev => ({ ...prev, [integrationId]: true }));
    try {
      const response = await fetchWithTenant(`/api/vms/integrations/${integrationId}/users`);
      if (response.ok) {
        const data = await response.json();
        setIntegrationUsers(prev => ({ ...prev, [integrationId]: data.users || [] }));
      }
    } catch (error) {
      console.error(`Failed to load users for integration ${integrationId}:`, error);
    } finally {
      setLoadingIntegrationUsers(prev => ({ ...prev, [integrationId]: false }));
    }
  };

  const loadUserVmsLinks = async (userId) => {
    try {
      const response = await fetchWithTenant(`/api/vms/users/${userId}/links`);
      if (response.ok) {
        const data = await response.json();
        setUserVmsLinks(prev => ({ ...prev, [userId]: data.links || [] }));
      }
    } catch (error) {
      console.error(`Failed to load VMS links for user ${userId}:`, error);
    }
  };

  const handleLinkVmsUser = async (userId, integrationId, vmsUserId, vmsUsername) => {
    try {
      // Find the integration to check its type
      const integration = vmsIntegrations.find(i => i.id === integrationId);
      const isWaveVms = integration?.type === 'WAVE_VMS';

      if (vmsUserId) {
        // Create link
        const response = await fetchWithTenant(`/api/vms/users/${userId}/links`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ integrationId, vmsUserId, vmsUsername })
        });
        if (response.ok) {
          toast.success('VMS user linked successfully');
          loadUserVmsLinks(userId);

          // HYBRID: For Wave VMS, also update legacy waveUserId field
          if (isWaveVms) {
            const user = users.find(u => u.id === userId);
            if (user) {
              await fetch(`/api/users/${userId}/wave-link`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ waveUserId: vmsUserId })
              });
              // Reload cameras using legacy system
              loadUserCameras(userId, vmsUserId);
              // Update local state
              setUsers(prev => prev.map(u => u.id === userId ? { ...u, waveUserId: vmsUserId } : u));
            }
          }
        } else {
          const error = await response.json();
          toast.error(error.message || 'Failed to link VMS user');
        }
      } else {
        // Remove link
        const response = await fetchWithTenant(`/api/vms/users/${userId}/links/${integrationId}`, {
          method: 'DELETE'
        });
        if (response.ok) {
          toast.success('VMS user unlinked');
          loadUserVmsLinks(userId);

          // HYBRID: For Wave VMS, also clear legacy waveUserId field
          if (isWaveVms) {
            const user = users.find(u => u.id === userId);
            if (user) {
              await fetch(`/api/users/${userId}/wave-link`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ waveUserId: null })
              });
              // Clear cameras
              setUserCameras(prev => ({ ...prev, [userId]: [] }));
              // Update local state
              setUsers(prev => prev.map(u => u.id === userId ? { ...u, waveUserId: null } : u));
            }
          }
        }
      }
    } catch (error) {
      toast.error('Failed to update VMS link');
    }
  };

  const handleRefreshVmsPermissions = async (userId, integrationId) => {
    try {
      const response = await fetchWithTenant(`/api/vms/users/${userId}/links/${integrationId}/refresh`, {
        method: 'POST'
      });
      if (response.ok) {
        toast.success('Permissions refreshed');
        loadUserVmsLinks(userId);
      }
    } catch (error) {
      toast.error('Failed to refresh permissions');
    }
  };

  const getUserVmsLink = (userId, integrationId) => {
    const links = userVmsLinks[userId] || [];
    return links.find(l => l.integrationId === integrationId);
  };

  const getIntegrationIcon = (type) => {
    switch(type) {
      case 'WAVE_VMS': return Video;
      case 'MIRASYS_VMS': return Server;
      case 'MILESTONE_VMS': return Video;
      case 'GENETEC_VMS': return Video;
      default: return Video;
    }
  };

  const getIntegrationColor = (type) => {
    switch(type) {
      case 'WAVE_VMS': return { bg: 'rgba(59, 130, 246, 0.1)', border: 'rgba(59, 130, 246, 0.3)', text: '#3b82f6' };
      case 'MIRASYS_VMS': return { bg: 'rgba(168, 85, 247, 0.1)', border: 'rgba(168, 85, 247, 0.3)', text: '#a855f7' };
      case 'MILESTONE_VMS': return { bg: 'rgba(100, 116, 139, 0.1)', border: 'rgba(100, 116, 139, 0.3)', text: '#64748B' };
      case 'GENETEC_VMS': return { bg: 'rgba(20, 184, 166, 0.1)', border: 'rgba(20, 184, 166, 0.3)', text: '#14b8a6' };
      default: return { bg: 'rgba(107, 114, 128, 0.1)', border: 'rgba(107, 114, 128, 0.3)', text: '#6b7280' };
    }
  };


  const loadTenants = async () => {
    try {
      const response = await fetch('/api/platform/tenants', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setTenants(data || []);
      }
    } catch (error) {
      console.error('Failed to load tenants:', error);
    }
  };

  const loadUserReaders = async (userId, feenicsUserId) => {
    if (!feenicsUserId) return;
    
    setLoadingReaders(prev => ({ ...prev, [userId]: true }));
    try {
      // For now, just show linked status - full reader list comes from Feenics
      setUserReaders(prev => ({ ...prev, [userId]: { linked: true } }));
    } catch (error) {
      console.error('Failed to load readers:', error);
    } finally {
      setLoadingReaders(prev => ({ ...prev, [userId]: false }));
    }
  };

  const loadUserCameras = async (userId, waveUserId) => {
    if (!waveUserId) return;
    
    setLoadingCameras(prev => ({ ...prev, [userId]: true }));
    try {
      const response = await fetchWithTenant(`/api/wave/users/${waveUserId}/cameras`);
      const data = await response.json();
      setUserCameras(prev => ({ ...prev, [userId]: data.cameras || [] }));
    } catch (error) {
      console.error('Failed to load cameras:', error);
      setUserCameras(prev => ({ ...prev, [userId]: [] }));
    } finally {
      setLoadingCameras(prev => ({ ...prev, [userId]: false }));
    }
  };

  const toggleUserExpand = (user) => {
    if (expandedUser === user.id) {
      setExpandedUser(null);
    } else {
      setExpandedUser(user.id);
      loadUserVmsLinks(user.id);
      if (user.waveUserId && !userCameras[user.id]) {
        loadUserCameras(user.id, user.waveUserId);
      }
    }
  };

  const handleAddUser = async () => {
    if (!newUser.username || !newUser.password) {
      toast.error('Username and password are required');
      return;
    }

    try {
      const response = await fetch('/api/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newUser)
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('User created successfully');
        setUsers([...users, data.user]);
        setShowAddUser(false);
        setNewUser({ username: '', password: '', name: '', role: 'viewer', waveUserId: '', feenicsUserId: '', canLockdown: false, canAnswerIntercom: false, canActivateShabbat: false });
      } else {
        toast.error(data.error || 'Failed to create user');
      }
    } catch (error) {
      toast.error('Failed to create user');
    }
  };

  const handleUpdateUser = async (user) => {
    try {
      const response = await fetch(`/api/users/${user.id}`, {

        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(user)
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('User updated successfully');
        setUsers(users.map(u => u.id === user.id ? data.user : u));
        setEditingUser(null);
        
        // Reload cameras if wave user changed
        if (user.waveUserId) {
          loadUserCameras(user.id, user.waveUserId);
        } else {
          setUserCameras(prev => ({ ...prev, [user.id]: [] }));
        }
      } else {
        toast.error(data.error || 'Failed to update user');
      }
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleLinkFeenicsUser = async (userId, feenicsUserId) => {
    try {
      const response = await fetch(`/api/users/${userId}/feenics-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ feenicsUserId })
      });

      if (response.ok) {
        setUsers(users.map(u => 
          u.id === userId ? { ...u, feenicsUserId } : u
        ));
        toast.success(feenicsUserId ? 'Feenics user linked' : 'Feenics user unlinked');
      } else {
        toast.error('Failed to update Feenics link');
      }
    } catch (error) {
      toast.error('Failed to update Feenics link');
    }
  };

  const handleToggleLockdown = async (userId, canLockdown) => {
    try {
      const user = users.find(u => u.id === userId);
      const response = await fetch(`/api/users/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ...user, canLockdown })
      });

      if (response.ok) {
        setUsers(users.map(u => 
          u.id === userId ? { ...u, canLockdown } : u
        ));
        toast.success(canLockdown ? 'Lockdown permission enabled' : 'Lockdown permission disabled');
      } else {
        toast.error('Failed to update lockdown permission');
      }
    } catch (error) {
      toast.error('Failed to update lockdown permission');
    }
  };

  const handleToggleIntercom = async (userId, canAnswerIntercom) => {
    try {
      const user = users.find(u => u.id === userId);
      const response = await fetch(`/api/users/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ...user, canAnswerIntercom })
      });

      if (response.ok) {
        setUsers(users.map(u => 
          u.id === userId ? { ...u, canAnswerIntercom } : u
        ));
        toast.success(canAnswerIntercom ? 'Intercom permission enabled' : 'Intercom permission disabled');
      } else {
        toast.error('Failed to update intercom permission');
      }
    } catch (error) {
      toast.error('Failed to update intercom permission');
    }
  };

  const handleToggleShabbat = async (userId, canActivateShabbat) => {
    try {
      const user = users.find(u => u.id === userId);
      const response = await fetch(`/api/users/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ...user, canActivateShabbat })
      });

      if (response.ok) {
        setUsers(users.map(u =>
          u.id === userId ? { ...u, canActivateShabbat } : u
        ));
        toast.success(canActivateShabbat ? 'Shabbat permission enabled' : 'Shabbat permission disabled');
      } else {
        toast.error('Failed to update Shabbat permission');
      }
    } catch (error) {
      toast.error('Failed to update Shabbat permission');
    }
  };

  const handleToggleMdPermission = async (userId, tenantId, field, value) => {
    try {
      const response = await fetch(`/api/platform/users/${userId}/tenant-access/${tenantId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ [field]: value })
      });

      if (response.ok) {
        setUsers(users.map(u => {
          if (u.id === userId) {
            return {
              ...u,
              tenantAccess: u.tenantAccess.map(ta =>
                ta.tenantId === tenantId ? { ...ta, [field]: value } : ta
              )
            };
          }
          return u;
        }));
        const labels = {
          canViewMD: 'View Monitor',
          canManageAgents: 'Manage Agents',
          canManageAlerts: 'Manage Alerts',
          canPromoteDevices: 'Promote Devices',
          canViewNetworkMap: 'Network Map',
          canAcknowledgeAlerts: 'Acknowledge Alerts',
          canViewEntry: 'View Entry',
          canManageEntryDevices: 'Manage Entry Devices',
          canManageEntryProfiles: 'Manage Entry Profiles',
          canManageEntryUsers: 'Manage Entry Users',
          canViewEntryEvents: 'View Entry Events'
        };
        toast.success(`${labels[field]} ${value ? 'enabled' : 'disabled'}`);
      } else {
        toast.error('Failed to update permission');
      }
    } catch (error) {
      toast.error('Failed to update permission');
    }
  };

  const handleLinkWaveUser = async (userId, waveUserId) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;

    try {
      const response = await fetch(`/api/users/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ...user, waveUserId })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(waveUserId ? 'Wave user linked' : 'Wave user unlinked');
        setUsers(users.map(u => u.id === userId ? data.user : u));
        
        if (waveUserId) {
          loadUserCameras(userId, waveUserId);
        } else {
          setUserCameras(prev => ({ ...prev, [userId]: [] }));
        }
      } else {
        toast.error(data.error || 'Failed to update user');
      }
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm('Are you sure you want to delete this user?')) return;

    try {
      const response = await fetch(`/api/users/${userId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('User deleted successfully');
        setUsers(users.filter(u => u.id !== userId));
      } else {
        toast.error(data.error || 'Failed to delete user');
      }
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin': return <Shield size={16} style={{ color: '#ef4444' }} />;
      case 'operator': return <UserCog size={16} style={{ color: '#3b82f6' }} />;
      default: return <Eye size={16} style={{ color: 'var(--text-tertiary)' }} />;
    }
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'admin': return 'badge-danger';
      case 'operator': return 'badge-primary';
      default: return 'badge-secondary';
    }
  };

  const getWaveUserName = (waveUserId) => {
    const waveUser = waveUsers.find(wu => wu.id === waveUserId);
    return waveUser ? (waveUser.name || waveUser.id) : waveUserId;
  };

  const getFeenicsUserName = (feenicsUserId) => {
    const feenicsUser = feenicsUsers.find(fu => fu.id === feenicsUserId);
    return feenicsUser ? (feenicsUser.name || feenicsUser.username) : feenicsUserId;
  };

  if (loading) {
    return <div className="loading-spinner" />;
  }

  return (
    <>
    <div className="user-management">
      <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
          <Users size={20} />
          User Management
        </h3>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button 
            className="btn btn-secondary btn-sm" 
            onClick={loadWaveUsers}
            disabled={loadingWaveUsers}
            title="Refresh Wave Users"
          >
            <RefreshCw size={14} className={loadingWaveUsers ? 'spin' : ''} />
            Wave
          </button>
          <button 
            className="btn btn-secondary btn-sm" 
            onClick={loadFeenicsUsers}
            disabled={loadingFeenicsUsers}
            title="Refresh Feenics Users"
          >
            <RefreshCw size={14} className={loadingFeenicsUsers ? 'spin' : ''} />
            Feenics
          </button>
          <button className="btn btn-primary btn-sm" onClick={() => setShowAddUser(true)}>
            <Plus size={16} /> Add User
          </button>
        </div>
      </div>

      {/* System Users Status */}
      <div style={{ 
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '12px',
        marginBottom: '16px'
      }}>
        <div style={{ 
          background: 'rgba(59, 130, 246, 0.1)', 
          border: '1px solid rgba(59, 130, 246, 0.2)',
          borderRadius: '8px',
          padding: '12px 16px'
        }}>
          <div style={{ fontSize: '13px', marginBottom: '4px' }}>
            <Camera size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
            <strong>{waveUsers.length}</strong> Wave VMS users
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
            {waveUsers.slice(0, 2).map(u => u.name || u.id).join(', ')}
            {waveUsers.length > 2 && ` +${waveUsers.length - 2} more`}
          </div>
        </div>
        <div style={{ 
          background: 'rgba(16, 185, 129, 0.1)', 
          border: '1px solid rgba(16, 185, 129, 0.2)',
          borderRadius: '8px',
          padding: '12px 16px'
        }}>
          <div style={{ fontSize: '13px', marginBottom: '4px' }}>
            <DoorOpen size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
            <strong>{feenicsUsers.length}</strong> Feenics users
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
            {feenicsUsers.slice(0, 2).map(u => u.name || u.username).join(', ')}
            {feenicsUsers.length > 2 && ` +${feenicsUsers.length - 2} more`}
          </div>
        </div>
      </div>

      {/* Add User Form */}
      {showAddUser && (
        <div className="card" style={{ marginBottom: '16px', background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
          <h4 style={{ marginBottom: '16px' }}>Add New User</h4>
          <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
            <div className="form-group">
              <label className="form-label">Username *</label>
              <input
                type="text"
                className="form-input"
                value={newUser.username}
                onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                placeholder="Username"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Password *</label>
              <input
                type="password"
                className="form-input"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                placeholder="Password"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Display Name</label>
              <input
                type="text"
                className="form-input"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                placeholder="Display Name"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Role</label>
              <select
                className="form-select"
                value={newUser.role}
                onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
              >
                <option value="viewer">Viewer (Read Only)</option>
                <option value="operator">Operator (Can Unlock Doors)</option>
                <option value="admin">Admin (Full Access)</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Link to Wave VMS User</label>
              <select
                className="form-select"
                value={newUser.waveUserId}
                onChange={(e) => setNewUser({ ...newUser, waveUserId: e.target.value })}
              >
                <option value="">-- Select Wave User --</option>
                {waveUsers.map(wu => (
                  <option key={wu.id} value={wu.id}>
                    {wu.name || wu.id} {wu.isAdmin ? '(Admin)' : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Link to Feenics User</label>
              <select
                className="form-select"
                value={newUser.feenicsUserId}
                onChange={(e) => setNewUser({ ...newUser, feenicsUserId: e.target.value })}
              >
                <option value="">-- Select Feenics User --</option>
                {feenicsUsers.map(fu => (
                  <option key={fu.id} value={fu.id}>
                    {fu.name || fu.username} {fu.groups?.length > 0 ? `(${fu.groups[0]})` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                id="newUserCanLockdown"
                checked={newUser.canLockdown}
                onChange={(e) => setNewUser({ ...newUser, canLockdown: e.target.checked })}
                style={{ width: '18px', height: '18px' }}
              />
              <label htmlFor="newUserCanLockdown" className="form-label" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                <AlertTriangle size={14} color="#f59e0b" />
                Emergency Lockdown Permission
              </label>
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                id="newUserCanAnswerIntercom"
                checked={newUser.canAnswerIntercom}
                onChange={(e) => setNewUser({ ...newUser, canAnswerIntercom: e.target.checked })}
                style={{ width: '18px', height: '18px' }}
              />
              <label htmlFor="newUserCanAnswerIntercom" className="form-label" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Phone size={14} color="#8b5cf6" />
                Intercom Notifications
              </label>
            </div>
            <div className="form-group">
              <label className="form-label">Link to Feenics User</label>
              <select
                className="form-select"
                value={newUser.feenicsUserId}
                onChange={(e) => setNewUser({ ...newUser, feenicsUserId: e.target.value })}
              >
                <option value="">-- Select Feenics User --</option>
                {feenicsUsers.map(fu => (
                  <option key={fu.id} value={fu.id}>
                    {fu.name || fu.username} {fu.groups?.length > 0 ? `(${fu.groups[0]})` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-actions" style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
            <button className="btn btn-primary" onClick={handleAddUser}>
              <Save size={16} /> Create User
            </button>
            <button className="btn btn-secondary" onClick={() => setShowAddUser(false)}>
              <X size={16} /> Cancel
            </button>
          </div>
        </div>
      )}

      {/* Tenant Filter */}
      {tenants.length > 0 && (
        <div className="user-filter-bar" style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          marginBottom: '1rem',
          padding: '0.75rem 1rem',
          background: 'rgba(255,255,255,0.03)',
          borderRadius: '8px'
        }}>
          <label style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Filter by Tenant:</label>
          <select 
            value={filterTenant} 
            onChange={(e) => setFilterTenant(e.target.value)}
            style={{
              background: 'var(--bg-surface)',
              border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: '6px',
              padding: '0.5rem 2rem 0.5rem 0.75rem',
              color: '#e5e7eb',
              fontSize: '0.85rem',
              cursor: 'pointer',
              minWidth: '180px'
            }}
          >
            <option value="all">All Tenants</option>
            {tenants.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
          {filterTenant !== 'all' && (
            <span style={{ color: 'var(--text-tertiary)', fontSize: '0.85rem' }}>
              Showing users with access to selected tenant
            </span>
          )}
        </div>
      )}

      {/* Users List */}
      <div className="users-list">
        {users.length === 0 ? (
          <div className="empty-state">
            <Users size={32} />
            <p>No users found</p>
          </div>
        ) : (
          users
            .filter(user => {
              if (filterTenant === 'all') return true;
              // Check if user has access to selected tenant (for DB users)
              if (user.tenantAccess) {
                return user.tenantAccess.some(ta => ta.tenantId === filterTenant);
              }
              // For JSON users without tenant info, show all
              return true;
            })
            .map(user => (
            <div key={user.id} className="user-card" style={{
              background: 'rgba(255,255,255,0.05)',
              borderRadius: '8px',
              marginBottom: '8px',
              overflow: 'hidden'
            }}>
              {/* User Header */}
              <div style={{
                padding: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer'
              }} onClick={() => toggleUserExpand(user)}>
                {editingUser?.id === user.id ? (
                  // Edit Mode
                  <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '8px', alignItems: 'center' }}
                    onClick={(e) => e.stopPropagation()}>
                    <input
                      type="text"
                      className="form-input"
                      value={editingUser.name || ''}
                      onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                      placeholder="Display Name"
                    />
                    <select
                      className="form-select"
                      value={editingUser.role}
                      onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
                    >
                      <option value="viewer">Viewer</option>
                      <option value="operator">Operator</option>
                      <option value="admin">Admin</option>
                    </select>
                    <input
                      type="password"
                      className="form-input"
                      value={editingUser.newPassword || ''}
                      onChange={(e) => setEditingUser({ ...editingUser, password: e.target.value })}
                      placeholder="New Password (optional)"
                    />
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <button className="btn btn-primary btn-sm" onClick={() => handleUpdateUser(editingUser)}>
                        <Save size={14} />
                      </button>
                      <button className="btn btn-secondary btn-sm" onClick={() => setEditingUser(null)}>
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                ) : (
                  // View Mode
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, flexWrap: 'wrap' }}>
                      {getRoleIcon(user.role)}
                      <div>
                        <div style={{ fontWeight: 500 }}>{user.name || user.username}</div>
                        <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>@{user.username}</div>
                      </div>
                      <span className={`badge ${getRoleBadgeClass(user.role)}`} style={{
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        textTransform: 'uppercase'
                      }}>
                        {user.role}
                      </span>
                      {user.waveUserId && (
                        <span className="badge badge-success" style={{
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          <Camera size={10} />
                          {getWaveUserName(user.waveUserId)}
                        </span>
                      )}
                      {user.feenicsUserId && (
                        <span className="badge" style={{
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          background: 'rgba(16, 185, 129, 0.2)',
                          color: '#10b981'
                        }}>
                          <DoorOpen size={10} />
                          {getFeenicsUserName(user.feenicsUserId)}
                        </span>
                      )}
                      {user.canLockdown && (
                        <span className="badge" style={{
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          background: 'rgba(245, 158, 11, 0.2)',
                          color: '#f59e0b'
                        }}>
                          <AlertTriangle size={10} />
                          Lockdown
                        </span>
                      )}
                      {user.canAnswerIntercom && (
                        <span className="badge" style={{
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          background: 'rgba(139, 92, 246, 0.2)',
                          color: '#8b5cf6'
                        }}>
                          <Phone size={10} />
                          Intercom
                        </span>
                      )}
                      {user.canActivateShabbat && (
                        <span className="badge" style={{
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          background: 'rgba(139, 92, 246, 0.2)',
                          color: '#8b5cf6'
                        }}>
                          <Sunset size={10} />
                          Shabbat
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
                      <button className="btn btn-secondary btn-sm" onClick={() => setEditingUser({ ...user })}>
                        <Edit2 size={14} />
                      </button>
                      <button className="btn btn-secondary btn-sm" onClick={() => setSessionsModalUser(user)} title="Manage Sessions">
                        <Monitor size={14} />
                      </button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDeleteUser(user.id)}>
                        <Trash2 size={14} />
                      </button>
                      {expandedUser === user.id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </div>
                  </>
                )}
              </div>

              {/* Expanded Content */}
              {expandedUser === user.id && !editingUser && (
                <div style={{
                  padding: '16px',
                  borderTop: '1px solid rgba(255,255,255,0.1)',
                  background: 'rgba(0,0,0,0.2)'
                }}>
                  {/* ==================== VMS INTEGRATIONS SECTION ==================== */}
                  <div style={{ marginBottom: '20px' }}>
                    <h4 style={{ 
                      fontSize: '12px', 
                      fontWeight: '600', 
                      color: 'var(--text-secondary)', 
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      marginBottom: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <Video size={14} />
                      VMS Integrations (Camera Access)
                    </h4>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
                      {vmsIntegrations.map(integration => {
                        const IntegrationIcon = getIntegrationIcon(integration.type);
                        const colors = getIntegrationColor(integration.type);
                        const link = getUserVmsLink(user.id, integration.id);
                        const users = integrationUsers[integration.id] || [];
                        const isLoading = loadingIntegrationUsers[integration.id];
                        
                        return (
                          <div key={integration.id} style={{
                            background: colors.bg,
                            border: `1px solid ${colors.border}`,
                            borderRadius: '8px',
                            padding: '12px',
                            position: 'relative'
                          }}>
                            {/* Card Header */}
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <IntegrationIcon size={16} style={{ color: colors.text }} />
                                <span style={{ fontWeight: '500', fontSize: '14px' }}>{integration.name}</span>
                              </div>
                              {link ? (
                                <CheckCircle size={16} style={{ color: '#10b981' }} />
                              ) : (
                                <XCircle size={16} style={{ color: 'var(--text-tertiary)' }} />
                              )}
                            </div>
                            
                            {/* User Selection */}
                            <select
                              className="form-select"
                              value={link?.vmsUserId || ''}
                              onChange={(e) => {
                                const selectedUser = users.find(u => u.id === e.target.value);
                                handleLinkVmsUser(user.id, integration.id, e.target.value, selectedUser?.displayName || selectedUser?.username || selectedUser?.id);
                              }}
                              disabled={isLoading}
                              style={{ 
                                width: '100%', 
                                fontSize: '13px',
                                    background: 'var(--bg-surface)',
                                    border: '1px solid rgba(255,255,255,0.15)',
                                    borderRadius: '6px',
                                    color: '#e5e7eb'
                              }}
                            >
                              <option value="">-- Not Linked --</option>
                              {users.map(vu => (
                                <option key={vu.id} value={vu.id}>
                                  {vu.displayName || vu.username || vu.id} {vu.isAdmin ? '(Admin)' : ''}
                                </option>
                              ))}
                            </select>
                            
                            {/* Link Info Footer */}
                            {link && (
                              <div style={{ 
                                marginTop: '10px', 
                                paddingTop: '10px', 
                                borderTop: '1px solid rgba(255,255,255,0.1)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                              }}>
                                <div style={{ fontSize: '11px', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <Clock size={10} />
                                  {link.permissionsCachedAt ? new Date(link.permissionsCachedAt).toLocaleDateString() : 'Never synced'}
                                </div>
                                <div style={{ display: 'flex', gap: '4px' }}>
                                  <button
                                    className="btn btn-secondary btn-sm"
                                    onClick={() => handleRefreshVmsPermissions(user.id, integration.id)}
                                    title="Refresh Permissions"
                                    style={{ padding: '4px 6px' }}
                                  >
                                    <RefreshCw size={12} />
                                  </button>
                                  <button
                                    className="btn btn-secondary btn-sm"
                                    onClick={() => handleLinkVmsUser(user.id, integration.id, '', '')}
                                    title="Unlink"
                                    style={{ padding: '4px 6px' }}
                                  >
                                    <Unlink size={12} />
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      
                      {vmsIntegrations.length === 0 && (
                        <div style={{ color: 'var(--text-tertiary)', fontSize: '13px', fontStyle: 'italic' }}>
                          No VMS integrations configured
                        </div>
                      )}
                    </div>
                  </div>

                  {/* ==================== ACCESS CONTROL SECTION ==================== */}
                  <div style={{ marginBottom: '20px' }}>
                    <h4 style={{ 
                      fontSize: '12px', 
                      fontWeight: '600', 
                      color: 'var(--text-secondary)', 
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      marginBottom: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <DoorOpen size={14} />
                      Access Control (Door Access)
                    </h4>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
                      {/* Feenics Card */}
                      <div style={{
                        background: user.feenicsUserId ? 'rgba(16, 185, 129, 0.1)' : 'rgba(255,255,255,0.03)',
                        border: user.feenicsUserId ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '8px',
                        padding: '12px'
                      }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <DoorOpen size={16} style={{ color: '#10b981' }} />
                          <span style={{ fontWeight: '500', fontSize: '14px' }}>Feenics Access Control</span>
                        </div>
                        {user.feenicsUserId ? (
                          <CheckCircle size={16} style={{ color: '#10b981' }} />
                        ) : (
                          <XCircle size={16} style={{ color: 'var(--text-tertiary)' }} />
                        )}
                      </div>
                      
                      <div style={{ display: 'flex', gap: '8px' }}>
                          <select
                            className="form-select"
                            value={user.feenicsUserId || ''}
                            onChange={(e) => handleLinkFeenicsUser(user.id, e.target.value)}
                            style={{
                              flex: 1,
                              fontSize: '13px',
                              background: 'var(--bg-surface)',
                              border: '1px solid rgba(255,255,255,0.15)',
                              borderRadius: '6px',
                              color: '#e5e7eb'
                            }}
                          >
                            <option value="">-- Not Linked --</option>
                            {feenicsUsers.map(fu => (
                              <option key={fu.id} value={fu.id}>
                              {fu.name || fu.username} {fu.groups?.length > 0 ? `(${fu.groups[0]})` : ''}
                            </option>
                          ))}
                        </select>
                        {user.feenicsUserId && (
                          <button 
                            className="btn btn-secondary btn-sm"
                            onClick={() => handleLinkFeenicsUser(user.id, '')}
                            title="Unlink Feenics User"
                            style={{ padding: '4px 6px' }}
                          >
                            <Unlink size={12} />
                          </button>
                        )}
                      </div>
                      
                      {user.feenicsUserId && (
                        <div style={{ marginTop: '8px', fontSize: '11px', color: '#10b981', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Link2 size={10} />
                          Door access controlled by Feenics permissions
                        </div>
                      )}
                      {!user.feenicsUserId && user.role !== 'admin' && (
                        <div style={{ marginTop: '8px', fontSize: '11px', color: '#f59e0b', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          ⚠️ No access to doors
                        </div>
                      )}
                      </div>
                    </div>
                  </div>

                  

                  {/* ==================== SPECIAL PERMISSIONS SECTION ==================== */}
                  <div style={{ marginBottom: '20px' }}>
                    <h4 style={{ 
                      fontSize: '12px', 
                      fontWeight: '600', 
                      color: 'var(--text-secondary)', 
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      marginBottom: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <Shield size={14} />
                      Special Permissions
                    </h4>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
                      {/* Emergency Lockdown Permission */}
                      <div style={{ 
                        padding: '12px',
                        background: user.canLockdown ? 'rgba(245, 158, 11, 0.1)' : 'rgba(255,255,255,0.03)',
                        border: user.canLockdown ? '1px solid rgba(245, 158, 11, 0.3)' : '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '8px'
                      }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <label className="form-label" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <AlertTriangle size={16} color="#f59e0b" />
                        <div>
                          <div>Emergency Lockdown Permission</div>
                          <div style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 'normal' }}>
                            Allow user to initiate facility-wide emergency lockdown
                          </div>
                        </div>
                      </label>
                      <button
                        className={`btn btn-sm ${user.canLockdown ? 'btn-warning' : 'btn-secondary'}`}
                        onClick={() => handleToggleLockdown(user.id, !user.canLockdown)}
                        style={{ minWidth: '80px' }}
                      >
                        {user.canLockdown ? 'Enabled' : 'Disabled'}
                      </button>
                    </div>
                  </div>

                  {/* Intercom Notification Permission */}
                  <div style={{ 
                    marginBottom: '16px',
                    padding: '12px',
                    background: user.canAnswerIntercom ? 'rgba(139, 92, 246, 0.1)' : 'rgba(255,255,255,0.03)',
                    border: user.canAnswerIntercom ? '1px solid rgba(139, 92, 246, 0.3)' : '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '8px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <label className="form-label" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Phone size={16} color="#8b5cf6" />
                        <div>
                          <div>Intercom Notifications</div>
                          <div style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 'normal' }}>
                            Receive doorbell notifications and unlock doors remotely
                          </div>
                        </div>
                      </label>
                      <button
                        className={`btn btn-sm ${user.canAnswerIntercom ? 'btn-primary' : 'btn-secondary'}`}
                        onClick={() => handleToggleIntercom(user.id, !user.canAnswerIntercom)}
                        style={{ minWidth: '80px' }}
                      >
                        {user.canAnswerIntercom ? 'Enabled' : 'Disabled'}
                      </button>
                    </div>
                  </div>

                  {/* Shabbat Mode Permission */}
                  <div style={{
                    marginBottom: '16px',
                    padding: '12px',
                    background: user.canActivateShabbat ? 'rgba(139, 92, 246, 0.1)' : 'rgba(255,255,255,0.03)',
                    border: user.canActivateShabbat ? '1px solid rgba(139, 92, 246, 0.3)' : '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '8px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <label className="form-label" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Sunset size={16} color="#8b5cf6" />
                        <div>
                          <div>Shabbat Mode</div>
                          <div style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 'normal' }}>
                            Manually activate/deactivate Shabbat mode
                          </div>
                        </div>
                      </label>
                      <button
                        className={`btn btn-sm ${user.canActivateShabbat ? 'btn-primary' : 'btn-secondary'}`}
                        onClick={() => handleToggleShabbat(user.id, !user.canActivateShabbat)}
                        style={{ minWidth: '80px' }}
                      >
                        {user.canActivateShabbat ? 'Enabled' : 'Disabled'}
                      </button>
                    </div>
                  </div>
                    </div>
                  </div>

                  
                  {/* CCM Monitor Permissions */}
                  {user.tenantAccess && user.tenantAccess.length > 0 && (
                    <div style={{ gridColumn: '1 / -1', marginTop: '8px' }}>
                      <h4 style={{
                        color: 'var(--text-primary)',
                        fontSize: '0.85rem',
                        marginBottom: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <Activity size={14} color="#00d4aa" />
                        CCM Monitor Permissions
                      </h4>
                      {user.tenantAccess.map(ta => (
                        <div key={ta.tenantId} style={{ marginBottom: '12px' }}>
                          {user.tenantAccess.length > 1 && (
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '6px' }}>
                              {ta.tenant?.name || ta.tenantId}
                            </div>
                          )}
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '8px' }}>
                            {[
                              { field: 'canViewMD', label: 'View CCM Monitor', desc: 'Access the monitoring dashboard', color: '#00d4aa' },
                              { field: 'canManageAgents', label: 'Manage Agents', desc: 'Pair agents, trigger scans, maintenance', color: '#3b82f6' },
                              { field: 'canManageAlerts', label: 'Manage Alerts', desc: 'Configure alert rules and thresholds', color: '#f59e0b' },
                              { field: 'canPromoteDevices', label: 'Promote Devices', desc: 'Add devices to active monitoring', color: '#8b5cf6' },
                              { field: 'canViewNetworkMap', label: 'Network Map', desc: 'View network topology', color: '#6366f1' },
                              { field: 'canAcknowledgeAlerts', label: 'Ack Alerts', desc: 'Acknowledge and resolve alerts', color: '#ec4899' },
                            ].map(perm => (
                              <div key={perm.field} style={{
                                padding: '10px 12px',
                                background: ta[perm.field] ? `${perm.color}15` : 'rgba(255,255,255,0.03)',
                                border: ta[perm.field] ? `1px solid ${perm.color}40` : '1px solid rgba(255,255,255,0.1)',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '8px'
                              }}>
                                <div>
                                  <div style={{ fontSize: '0.8rem', fontWeight: 500, color: 'var(--text-primary)' }}>{perm.label}</div>
                                  <div style={{ fontSize: '0.65rem', color: 'var(--text-secondary)' }}>{perm.desc}</div>
                                </div>
                                <button
                                  className={`btn btn-sm ${ta[perm.field] ? 'btn-success' : 'btn-secondary'}`}
                                  onClick={() => handleToggleMdPermission(user.id, ta.tenantId, perm.field, !ta[perm.field])}
                                  style={{ minWidth: '70px', fontSize: '0.75rem' }}
                                >
                                  {ta[perm.field] ? 'On' : 'Off'}
                                </button>
                              </div>
                            ))}
                          </div>
                          {ta.role === 'ADMIN' && ta.canViewMD && (
                            <div style={{ fontSize: '0.7rem', color: '#00d4aa', marginTop: '6px', fontStyle: 'italic' }}>
                              Admins with View MD enabled automatically get all MD permissions
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Serverli Entry Permissions */}
                  {user.tenantAccess && user.tenantAccess.length > 0 && (
                    <div style={{ gridColumn: '1 / -1', marginTop: '8px' }}>
                      <h4 style={{
                        color: 'var(--text-primary)',
                        fontSize: '0.85rem',
                        marginBottom: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <Tablet size={14} color="#a855f7" />
                        Serverli Entry Permissions
                      </h4>
                      {user.tenantAccess.map(ta => (
                        <div key={'entry-' + ta.tenantId} style={{ marginBottom: '12px' }}>
                          {user.tenantAccess.length > 1 && (
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '6px' }}>
                              {ta.tenant?.name || ta.tenantId}
                            </div>
                          )}
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '8px' }}>
                            {[
                              { field: 'canViewEntry', label: 'View Entry', desc: 'Access the Entry fleet dashboard', color: '#a855f7' },
                              { field: 'canManageEntryDevices', label: 'Manage Devices', desc: 'Enroll, configure, and decommission tablets', color: '#3b82f6' },
                              { field: 'canManageEntryProfiles', label: 'Manage Profiles', desc: 'Create and edit configuration profiles', color: '#f59e0b' },
                              { field: 'canManageEntryUsers', label: 'Manage Users', desc: 'Register users in OnWatch and access control', color: '#ec4899' },
                              { field: 'canViewEntryEvents', label: 'View Events', desc: 'View access event logs and reports', color: '#06b6d4' },
                            ].map(perm => (
                              <div key={perm.field} style={{
                                padding: '10px 12px',
                                background: ta[perm.field] ? `${perm.color}15` : 'rgba(255,255,255,0.03)',
                                border: ta[perm.field] ? `1px solid ${perm.color}40` : '1px solid rgba(255,255,255,0.1)',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '8px'
                              }}>
                                <div>
                                  <div style={{ fontSize: '0.8rem', fontWeight: 500, color: 'var(--text-primary)' }}>{perm.label}</div>
                                  <div style={{ fontSize: '0.65rem', color: 'var(--text-secondary)' }}>{perm.desc}</div>
                                </div>
                                <button
                                  className={`btn btn-sm ${ta[perm.field] ? 'btn-success' : 'btn-secondary'}`}
                                  onClick={() => handleToggleMdPermission(user.id, ta.tenantId, perm.field, !ta[perm.field])}
                                  style={{ minWidth: '70px', fontSize: '0.75rem' }}
                                >
                                  {ta[perm.field] ? 'On' : 'Off'}
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Camera Access */}
                  {user.waveUserId && (
                    <div>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        marginBottom: '12px'
                      }}>
                        <label className="form-label" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Camera size={14} />
                          Camera Access
                        </label>
                        <button 
                          className="btn btn-secondary btn-sm"
                          onClick={() => loadUserCameras(user.id, user.waveUserId)}
                          disabled={loadingCameras[user.id]}
                        >
                          <RefreshCw size={12} className={loadingCameras[user.id] ? 'spin' : ''} />
                        </button>
                      </div>
                      
                      {loadingCameras[user.id] ? (
                        <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Loading cameras...</div>
                      ) : userCameras[user.id]?.length > 0 ? (
                        <div style={{ 
                          display: 'grid', 
                          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                          gap: '8px'
                        }}>
                          {userCameras[user.id].map(camera => (
                            <div key={camera.id} style={{
                              background: 'rgba(255,255,255,0.05)',
                              padding: '8px 12px',
                              borderRadius: '6px',
                              fontSize: '12px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px'
                            }}>
                              <Camera size={12} style={{ color: '#10b981' }} />
                              {camera.name}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{ 
                          color: 'var(--text-secondary)', 
                          fontSize: '13px',
                          padding: '12px',
                          background: 'rgba(255,255,255,0.02)',
                          borderRadius: '6px',
                          textAlign: 'center'
                        }}>
                          No cameras accessible or unable to load camera permissions
                        </div>
                      )}
                    </div>
                  )}

                  {!user.waveUserId && (
                    <div style={{ 
                      color: 'var(--text-secondary)', 
                      fontSize: '13px',
                      padding: '12px',
                      background: 'rgba(255,255,255,0.02)',
                      borderRadius: '6px',
                      textAlign: 'center'
                    }}>
                      Link a Wave VMS user to see their camera access
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
      {/* Admin User Sessions Modal */}
      {sessionsModalUser && (
        <AdminUserSessionsModal
          user={sessionsModalUser}
          onClose={() => setSessionsModalUser(null)}
        />
      )}
    </>
  );
};

export default UserManagement;
